export type YesNo = 'sim' | 'nao' | '';

export type MainGoal =
  | 'trabalho_subordinado'
  | 'procura_trabalho'
  | 'estudo'
  | 'empreendedorismo_d2'
  | 'nomade_digital_d8'
  | 'rendimentos_d7'
  | 'reagrupamento_familiar'
  | 'nacionalidade'
  | 'outro'
  | '';

export type ConditionalSection =
  | 'workD1'
  | 'studyD4'
  | 'd7'
  | 'd8'
  | 'd2'
  | 'familyReunion'
  | 'nationalityPt';

export interface VisaResponses {
  personal: {
    fullName: string;
    birthDate: string;
    nationality: string;
    maritalStatus: string;
    hasChildren: YesNo;
    childrenCount: string;
    email: string;
    phone: string;
    hasValidPassport: YesNo;
  };
  currentSituation: {
    inPortugal: YesNo;
    enteredLegally: YesNo;
    hasEntryStamp: YesNo;
    residencyStatus: string;
    hasAimaOrLegalProcess: YesNo;
  };
  objective: {
    mainGoal: MainGoal;
    otherGoal: string;
    bringFamily: YesNo;
    hasAccommodation: YesNo;
  };
  workD1: {
    hasJobOffer: YesNo;
    hasContractOrPromise: YesNo;
    profession: string;
    hasProvenExperience: YesNo;
    hasUpdatedCv: YesNo;
  };
  studyD4: {
    acceptedByInstitution: YesNo;
    intendedCourse: string;
    hasFinancialProof: YesNo;
    studyFundingSource: string;
  };
  d7: {
    hasPassiveIncome: YesNo;
    incomeSource: string;
    canProveOfficially: YesNo;
    hasSavings: YesNo;
  };
  d8: {
    worksRemotely: YesNo;
    worksForForeignCompany: YesNo;
    averageMonthlyIncome: string;
    hasContractsOrProof: YesNo;
  };
  d2: {
    hasCompany: YesNo;
    willOpenCompanyInPortugal: YesNo;
    hasBusinessPlan: YesNo;
    hasInvestmentCapital: YesNo;
  };
  familyReunion: {
    hasLegalizedRelative: YesNo;
    relationshipDegree: string;
    relativeStatus: string;
  };
  nationalityPt: {
    hasPortugueseRelative: YesNo;
    processStarted: YesNo;
    hasFamilyDocuments: YesNo;
  };
}

export const emptyResponses = (): VisaResponses => ({
  personal: {
    fullName: '',
    birthDate: '',
    nationality: '',
    maritalStatus: '',
    hasChildren: '',
    childrenCount: '',
    email: '',
    phone: '',
    hasValidPassport: ''
  },
  currentSituation: {
    inPortugal: '',
    enteredLegally: '',
    hasEntryStamp: '',
    residencyStatus: '',
    hasAimaOrLegalProcess: ''
  },
  objective: {
    mainGoal: '',
    otherGoal: '',
    bringFamily: '',
    hasAccommodation: ''
  },
  workD1: {
    hasJobOffer: '',
    hasContractOrPromise: '',
    profession: '',
    hasProvenExperience: '',
    hasUpdatedCv: ''
  },
  studyD4: {
    acceptedByInstitution: '',
    intendedCourse: '',
    hasFinancialProof: '',
    studyFundingSource: ''
  },
  d7: {
    hasPassiveIncome: '',
    incomeSource: '',
    canProveOfficially: '',
    hasSavings: ''
  },
  d8: {
    worksRemotely: '',
    worksForForeignCompany: '',
    averageMonthlyIncome: '',
    hasContractsOrProof: ''
  },
  d2: {
    hasCompany: '',
    willOpenCompanyInPortugal: '',
    hasBusinessPlan: '',
    hasInvestmentCapital: ''
  },
  familyReunion: {
    hasLegalizedRelative: '',
    relationshipDegree: '',
    relativeStatus: ''
  },
  nationalityPt: {
    hasPortugueseRelative: '',
    processStarted: '',
    hasFamilyDocuments: ''
  }
});

export const MAIN_GOAL_VALUES: MainGoal[] = [
  'trabalho_subordinado',
  'procura_trabalho',
  'estudo',
  'empreendedorismo_d2',
  'nomade_digital_d8',
  'rendimentos_d7',
  'reagrupamento_familiar',
  'nacionalidade',
  'outro'
];

export function getConditionalSections(goal: MainGoal): ConditionalSection[] {
  switch (goal) {
    case 'trabalho_subordinado':
    case 'procura_trabalho':
      return ['workD1'];
    case 'estudo':
      return ['studyD4'];
    case 'rendimentos_d7':
      return ['d7'];
    case 'nomade_digital_d8':
      return ['d8'];
    case 'empreendedorismo_d2':
      return ['d2'];
    case 'reagrupamento_familiar':
      return ['familyReunion'];
    case 'nacionalidade':
      return ['nationalityPt'];
    default:
      return [];
  }
}

export type WizardStepId =
  | 'personal'
  | 'situation'
  | 'objective'
  | ConditionalSection
  | 'review';

export function buildWizardSteps(goal: MainGoal): WizardStepId[] {
  const base: WizardStepId[] = ['personal', 'situation', 'objective'];
  const extra = getConditionalSections(goal);
  return [...base, ...extra, 'review'];
}

export type ReviewLine = { label: string; value: string };
export type ReviewSection = { title: string; lines: ReviewLine[] };

type VisaFormT = (key: string) => string;

function line(label: string, value: string | undefined | null): ReviewLine | null {
  const v = value?.toString().trim();
  if (!v) return null;
  return { label, value: v };
}

function lines(...items: (ReviewLine | null)[]): ReviewLine[] {
  return items.filter((x): x is ReviewLine => x !== null);
}

export function buildReviewSummary(data: VisaResponses, t: VisaFormT): ReviewSection[] {
  const L = (key: string) => t(`visaForm.reviewLabels.${key}`);
  const stepTitle = (step: WizardStepId) => t(`visaForm.steps.${step}`);
  const yn = (v: YesNo) => (v === 'sim' ? t('common.yes') : v === 'nao' ? t('common.no') : '');
  const marital = (status: string) =>
    status ? t(`visaForm.marital.${status}`) : '';
  const residency = (status: string) =>
    status ? t(`visaForm.residency.${status}`) : '';
  const relStatus = (status: string) =>
    status ? t(`visaForm.relativeStatus.${status}`) : '';

  const goalLabel = data.objective.mainGoal
    ? t(`visaForm.goals.${data.objective.mainGoal}`)
    : data.objective.otherGoal || '';

  const sections: ReviewSection[] = [];

  sections.push({
    title: stepTitle('personal'),
    lines: lines(
      line(L('fullName'), data.personal.fullName),
      line(L('birthDate'), data.personal.birthDate),
      line(L('nationality'), data.personal.nationality),
      line(L('maritalStatus'), marital(data.personal.maritalStatus)),
      line(L('hasChildren'), yn(data.personal.hasChildren)),
      data.personal.hasChildren === 'sim' ? line(L('childrenCount'), data.personal.childrenCount) : null,
      line(L('email'), data.personal.email),
      line(L('phone'), data.personal.phone),
      line(L('hasValidPassport'), yn(data.personal.hasValidPassport))
    )
  });

  const situationLines = lines(
    line(L('inPortugal'), yn(data.currentSituation.inPortugal)),
    line(L('hasAimaProcess'), yn(data.currentSituation.hasAimaOrLegalProcess))
  );
  if (data.currentSituation.inPortugal === 'sim') {
    situationLines.push(
      ...lines(
        line(L('enteredLegally'), yn(data.currentSituation.enteredLegally)),
        line(L('hasEntryStamp'), yn(data.currentSituation.hasEntryStamp)),
        line(L('situation'), residency(data.currentSituation.residencyStatus))
      )
    );
  }
  sections.push({ title: stepTitle('situation'), lines: situationLines });

  sections.push({
    title: stepTitle('objective'),
    lines: lines(
      line(L('mainGoal'), goalLabel),
      line(L('bringFamily'), yn(data.objective.bringFamily)),
      line(L('hasAccommodation'), yn(data.objective.hasAccommodation))
    )
  });

  const conditional = getConditionalSections(data.objective.mainGoal);
  if (conditional.includes('workD1')) {
    sections.push({
      title: stepTitle('workD1'),
      lines: lines(
        line(L('hasJobOffer'), yn(data.workD1.hasJobOffer)),
        line(L('hasContractOrPromise'), yn(data.workD1.hasContractOrPromise)),
        line(L('profession'), data.workD1.profession),
        line(L('hasProvenExperience'), yn(data.workD1.hasProvenExperience)),
        line(L('hasUpdatedCv'), yn(data.workD1.hasUpdatedCv))
      )
    });
  }
  if (conditional.includes('studyD4')) {
    sections.push({
      title: stepTitle('studyD4'),
      lines: lines(
        line(L('acceptedByInstitution'), yn(data.studyD4.acceptedByInstitution)),
        line(L('intendedCourse'), data.studyD4.intendedCourse),
        line(L('hasFinancialProof'), yn(data.studyD4.hasFinancialProof)),
        line(L('studyFundingSource'), data.studyD4.studyFundingSource)
      )
    });
  }
  if (conditional.includes('d7')) {
    sections.push({
      title: stepTitle('d7'),
      lines: lines(
        line(L('hasPassiveIncome'), yn(data.d7.hasPassiveIncome)),
        line(L('incomeSource'), data.d7.incomeSource),
        line(L('canProveOfficially'), yn(data.d7.canProveOfficially)),
        line(L('hasSavings'), yn(data.d7.hasSavings))
      )
    });
  }
  if (conditional.includes('d8')) {
    sections.push({
      title: stepTitle('d8'),
      lines: lines(
        line(L('worksRemotely'), yn(data.d8.worksRemotely)),
        line(L('worksForForeignCompany'), yn(data.d8.worksForForeignCompany)),
        line(L('averageMonthlyIncome'), data.d8.averageMonthlyIncome),
        line(L('hasContractsOrProof'), yn(data.d8.hasContractsOrProof))
      )
    });
  }
  if (conditional.includes('d2')) {
    sections.push({
      title: stepTitle('d2'),
      lines: lines(
        line(L('hasCompany'), yn(data.d2.hasCompany)),
        line(L('willOpenCompanyInPortugal'), yn(data.d2.willOpenCompanyInPortugal)),
        line(L('hasBusinessPlan'), yn(data.d2.hasBusinessPlan)),
        line(L('hasInvestmentCapital'), yn(data.d2.hasInvestmentCapital))
      )
    });
  }
  if (conditional.includes('familyReunion')) {
    sections.push({
      title: stepTitle('familyReunion'),
      lines: lines(
        line(L('hasLegalizedRelative'), yn(data.familyReunion.hasLegalizedRelative)),
        line(L('relationshipDegree'), data.familyReunion.relationshipDegree),
        line(L('relativeStatus'), relStatus(data.familyReunion.relativeStatus))
      )
    });
  }
  if (conditional.includes('nationalityPt')) {
    sections.push({
      title: stepTitle('nationalityPt'),
      lines: lines(
        line(L('hasPortugueseRelative'), yn(data.nationalityPt.hasPortugueseRelative)),
        line(L('processStarted'), yn(data.nationalityPt.processStarted)),
        line(L('hasFamilyDocuments'), yn(data.nationalityPt.hasFamilyDocuments))
      )
    });
  }

  return sections.filter((s) => s.lines.length > 0);
}
